import { useEffect, useRef, useState } from "react";

import { useDebounce, useMenu, usePagination } from "hooks";

import { MultiSelectResults, SearchWrapper } from "../shared";
import { FEMultiProps } from "../shared/types";

export default <T,>({
  id,
  itemKey,
  selectedItems,
  renderListItemLabel,
  onSearch,
  onSelect,
  buttonLabel,
  label,
  placeholder,
  hint,
  listHeader,
  footer,
  emptyState,
  minQueryLength = 0,
  perPage = 5,
  errorMessage,
  debounceTime = 750,
  reloadOnOpen = false,
  showPagination = true,
  showSelectAll = true,
}: Omit<FEMultiProps<T>, "isMultiSelect">): React.JSX.Element => {
  const [currentSelection, setCurrentSelection] = useState<T[]>(selectedItems);
  const [searchInput, setSearchInput] = useState("");
  const debouncedValue = useDebounce(searchInput, debounceTime);

  const { page, pages, pageItems, goToPage, resetItems } = usePagination<T>({
    items: [],
    perPage,
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useMenu(menuRef);
  const toggleIsOpen = () => setIsOpen(!isOpen);

  useEffect(() => {
    setCurrentSelection(selectedItems);
  }, [selectedItems.length]);

  useEffect(() => {
    if (debouncedValue.length < minQueryLength) return;

    onSearch(debouncedValue).then(resetItems);
  }, [debouncedValue]);

  useEffect(() => {
    if (!reloadOnOpen || !isOpen) return;

    onSearch(searchInput).then(resetItems);
  }, [isOpen]);

  return (
    <SearchWrapper
      menuRef={menuRef}
      id={id}
      defaultSearchInput={searchInput}
      onSearch={setSearchInput}
      isOpen={isOpen}
      toggleIsOpen={toggleIsOpen}
      buttonLabel={
        typeof buttonLabel === "function" ? buttonLabel(isOpen) : buttonLabel
      }
      label={label}
      placeholder={placeholder}
      hint={hint}
      isLoading={false}
      errorMessage={errorMessage}
    >
      <MultiSelectResults<T>
        itemKey={itemKey}
        items={pageItems}
        selection={currentSelection}
        setSelection={setCurrentSelection}
        listHeader={listHeader}
        page={page}
        pages={pages}
        setPage={goToPage}
        renderListItemLabel={renderListItemLabel}
        onSelect={onSelect}
        emptyState={emptyState}
        footer={footer}
        showPagination={showPagination}
        showSelectAll={showSelectAll}
      />
    </SearchWrapper>
  );
};
